export type Operation = 'mul' | 'div';
export type Tile = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
export type FactId = string;

export const ALL_TILES: readonly Tile[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
export const K_MIN = 2;
export const K_MAX = 10;

export interface Fact {
  /** `m:{tile}:{k}` или `d:{tile*k}:{tile}` — раздел 4.1 спеки. */
  id: FactId;
  op: Operation;
  /** Плитка, выбранная ребёнком. Для деления — делитель. */
  tile: Tile;
  /** Множитель, 2…10. Для деления — частное. */
  k: number;
  /** Левое число выражения на экране. */
  left: number;
  /** Правое число выражения на экране. */
  right: number;
  /** Верный ответ. */
  answer: number;
}

export function makeFact(op: Operation, tile: Tile, k: number): Fact {
  if (k < K_MIN || k > K_MAX) throw new Error(`makeFact: k вне 2..10: ${k}`);
  const product = tile * k;
  return op === 'mul'
    ? { id: `m:${tile}:${k}`, op, tile, k, left: tile, right: k, answer: product }
    : { id: `d:${product}:${tile}`, op, tile, k, left: product, right: tile, answer: k };
}

const TILE_MIN = 1;
const TILE_MAX = 10;

/** Единственная точка входа недоверенных строк: сохранённое состояние
 *  раздатчика, журнал попыток и (в следующем плане) сегмент URL. Без
 *  проверок `m:99:5` даёт плитку 99, а `d:7:2` — дробное частное 3.5,
 *  которое проходит проверку `makeFact`, потому что 3.5 лежит в
 *  диапазоне 2…10. */
export function parseFactId(id: FactId): Fact {
  const parts = id.split(':');
  if (parts.length !== 3) {
    throw new Error(`parseFactId: неверный формат идентификатора «${id}»`);
  }
  const [kind, a, b] = parts;
  if (kind !== 'm' && kind !== 'd') {
    throw new Error(`parseFactId: неизвестный префикс «${kind}» в идентификаторе «${id}»`);
  }

  const first = Number(a);
  const second = Number(b);
  if (!Number.isInteger(first) || !Number.isInteger(second)) {
    throw new Error(`parseFactId: нецелые компоненты в идентификаторе «${id}»`);
  }

  if (kind === 'm') {
    const tile = first;
    const k = second;
    if (tile < TILE_MIN || tile > TILE_MAX) {
      throw new Error(`parseFactId: плитка ${tile} вне диапазона ${TILE_MIN}..${TILE_MAX} в идентификаторе «${id}»`);
    }
    if (k < K_MIN || k > K_MAX) {
      throw new Error(`parseFactId: множитель ${k} вне диапазона ${K_MIN}..${K_MAX} в идентификаторе «${id}»`);
    }
    return makeFact('mul', tile as Tile, k);
  }

  const product = first;
  const tile = second;
  if (tile < TILE_MIN || tile > TILE_MAX) {
    throw new Error(`parseFactId: делитель ${tile} вне диапазона ${TILE_MIN}..${TILE_MAX} в идентификаторе «${id}»`);
  }
  if (product % tile !== 0) {
    throw new Error(
      `parseFactId: делимое ${product} не делится нацело на делитель ${tile} в идентификаторе «${id}»`,
    );
  }
  const k = product / tile;
  if (k < K_MIN || k > K_MAX) {
    throw new Error(`parseFactId: частное ${k} вне диапазона ${K_MIN}..${K_MAX} в идентификаторе «${id}»`);
  }
  return makeFact('div', tile as Tile, k);
}

/** Пул для выбранных плиток и одной операции. Связи из разных плиток
 *  не объединяются (§5 исходной спеки). */
export function poolFor(tiles: readonly Tile[], op: Operation): Fact[] {
  const facts: Fact[] = [];
  for (const tile of tiles) {
    for (let k = K_MIN; k <= K_MAX; k++) facts.push(makeFact(op, tile, k));
  }
  return facts;
}

/** Множество реально достижимых ответов — не отрезок между минимумом и
 *  максимумом (раздел 5.3). */
export function answerSet(facts: readonly Fact[]): number[] {
  return [...new Set(facts.map((f) => f.answer))].sort((a, b) => a - b);
}
